import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Lock, CalendarBlank } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const DAYS = [
  { key: 0, label: 'Lunedì', short: 'Lun' },
  { key: 1, label: 'Martedì', short: 'Mar' },
  { key: 2, label: 'Mercoledì', short: 'Mer' },
  { key: 3, label: 'Giovedì', short: 'Gio' },
  { key: 4, label: 'Venerdì', short: 'Ven' },
];

const STUDENT_COLORS = [
  '#DBEAFE', '#FCE7F3', '#DCFCE7', '#FEF3C7', '#EDE9FE',
  '#FEE2E2', '#CCFBF1', '#FED7AA', '#E9D5FF', '#BAE6FD',
];

function buildSlots() {
  const slots = [];
  for (let mins = 13 * 60; mins < 20 * 60; mins += 15) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return slots;
}

function toMin(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function getCurrentAcademicYear() {
  const now = new Date();
  const y = now.getFullYear();
  // Anno accademico parte da settembre
  return now.getMonth() >= 8 ? y : y - 1;
}

function getInitialAnno() {
  try {
    const params = new URLSearchParams(window.location.search);
    const y = parseInt(params.get('anno'));
    if (Number.isFinite(y) && y >= 2020 && y <= 2100) return y;
  } catch { /* noop */ }
  return getCurrentAcademicYear();
}

export default function PortineriaPage() {
  const [password, setPassword] = useState(() => localStorage.getItem('portineria_password') || '');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [company, setCompany] = useState({ nome: 'Fiori Musicali', logo_url: '' });
  const [day, setDay] = useState(() => {
    const jsDay = new Date().getDay(); // 0=dom ... 6=sab
    const map = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4 };
    return map[jsDay] ?? 0;
  });
  const [anno] = useState(getInitialAnno());
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [lessons, setLessons] = useState([]);

  // Carica company info per branding
  useEffect(() => {
    fetch('/api/settings/company').then(r => r.json()).then(c => setCompany(c)).catch(() => {});
  }, []);

  // Auto-login se password salvata
  useEffect(() => {
    if (password && !authed) {
      api.verifyPortineriaPassword(password)
        .then(() => setAuthed(true))
        .catch((err) => {
          // Rimuovi la password SOLO se il server dice che è sbagliata (401)
          // Se la portineria è chiusa (403) o c'è un errore di rete, mantieni la password
          if (err.response?.status === 401) {
            localStorage.removeItem('portineria_password');
            setPassword('');
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setChecking(true);
    try {
      await api.verifyPortineriaPassword(password.trim());
      localStorage.setItem('portineria_password', password.trim());
      setAuthed(true);
      toast.success('Accesso Portineria autorizzato');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore di autenticazione';
      toast.error(msg);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portineria_password');
    setPassword('');
    setAuthed(false);
    setTeachers([]);
    setLessons([]);
  };

  const load = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const res = await api.getPortineriaDayView(day, anno, password);
      setTeachers(res.data.teachers || []);
      setLessons(res.data.lessons || []);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        // Password errata → cancella e forza re-login
        toast.error(err.response?.data?.detail || 'Password non più valida');
        handleLogout();
      } else if (status === 403) {
        // Portineria chiusa dall'admin → mostra login ma NON cancella la password memorizzata
        toast.error(err.response?.data?.detail || 'Portineria momentaneamente chiusa');
        setAuthed(false);
        setTeachers([]);
        setLessons([]);
      } else {
        toast.error('Errore caricamento orario');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, day, anno]);

  useEffect(() => { load(); }, [load]);

  // Ora corrente per evidenziare il quarto d'ora in cui siamo
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000); // aggiorna ogni 30 s
    return () => clearInterval(id);
  }, []);
  const currentSlotIdx = useMemo(() => {
    // Evidenzia solo se il giorno mostrato è quello di oggi
    const jsDay = now.getDay();
    const map = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4 };
    if (map[jsDay] !== day) return -1;
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (nowMin < 13 * 60 || nowMin >= 20 * 60) return -1;
    return Math.floor((nowMin - 13 * 60) / 15);
  }, [now, day]);

  const slots = useMemo(buildSlots, []);
  const cellMap = useMemo(() => {
    const map = {};
    const studentIdxByTeacher = {};
    for (const l of lessons) {
      const startM = toMin(l.start_time);
      const endM = toMin(l.end_time);
      if (startM == null || endM == null || endM <= startM) continue;
      const startSlotIdx = Math.floor((startM - 13 * 60) / 15);
      const spanSlots = Math.max(1, Math.ceil((endM - startM) / 15));
      if (!studentIdxByTeacher[l.teacher_id]) studentIdxByTeacher[l.teacher_id] = {};
      const seen = studentIdxByTeacher[l.teacher_id];
      if (!(l.student_id in seen)) seen[l.student_id] = Object.keys(seen).length;
      const studentIdx = seen[l.student_id];
      for (let i = 0; i < spanSlots; i++) {
        const slotIdx = startSlotIdx + i;
        if (slotIdx < 0 || slotIdx >= slots.length) continue;
        map[`${l.teacher_id}|${slotIdx}`] = {
          student_name: l.student_name,
          is_start: i === 0,
          is_prova: l.is_prova,
          studentIdx,
          rowSpan: i === 0 ? Math.min(spanSlots, slots.length - startSlotIdx) : 0,
          start_time: l.start_time,
          end_time: l.end_time,
        };
      }
    }
    return map;
  }, [lessons, slots]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-white border border-[#E4E4E7] rounded-sm p-8 shadow-sm">
          <div className="text-center mb-6">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.nome} className="h-16 mx-auto mb-4 object-contain" />
            ) : (
              <Lock size={48} className="mx-auto text-[#2D2A4A] mb-3" />
            )}
            <h1 className="text-2xl font-bold font-['Work_Sans'] text-[#2D2A4A]">Portineria</h1>
            <p className="text-sm text-[#71717A] mt-1">{company.nome}</p>
            <p className="text-xs text-[#A1A1AA] mt-2">Accesso riservato al personale di segreteria</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="pwd" className="text-xs uppercase tracking-wider text-[#52525B]">Password</Label>
              <Input
                id="pwd"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci password"
                autoFocus
                className="mt-1 rounded-sm h-11"
                data-testid="portineria-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={checking || !password.trim()}
              className="w-full bg-[#2D2A4A] hover:bg-[#1F1D33] rounded-sm h-11"
              data-testid="portineria-login-btn"
            >
              {checking ? 'Verifica...' : 'Accedi'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-[#2D2A4A] text-white">
        <div className="w-full px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {company.logo_url && <img src={company.logo_url} alt="" className="h-8 object-contain" />}
            <div>
              <div className="font-bold font-['Work_Sans'] text-lg">Portineria · {company.nome}</div>
              <div className="text-xs opacity-70">Anno accademico {anno}/{String(anno + 1).slice(-2)}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-xs uppercase tracking-wider bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-sm"
            data-testid="portineria-logout-btn"
          >
            Esci
          </button>
        </div>
      </div>

      <div className="w-full px-3 sm:px-4 py-4">
        {/* Tab giorni */}
        <div className="flex items-center gap-1 mb-4 flex-wrap" data-testid="portineria-day-tabs">
          {DAYS.map(d => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDay(d.key)}
              className={`px-5 py-2.5 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors ${
                day === d.key ? 'bg-[#2D2A4A] text-white' : 'bg-white border border-[#E4E4E7] text-[#52525B] hover:bg-[#FAFAFA]'
              }`}
              data-testid={`portineria-day-${d.key}`}
            >
              {d.short}
            </button>
          ))}
          <div className="ml-auto text-xs text-[#52525B]">
            {teachers.length} {teachers.length === 1 ? 'insegnante' : 'insegnanti'} · {lessons.length} {lessons.length === 1 ? 'lezione' : 'lezioni'}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
        ) : teachers.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
            <CalendarBlank size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Nessuna lezione registrata per {DAYS.find(d => d.key === day)?.label}.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full border-collapse text-xs table-fixed" data-testid="portineria-table">
              <thead>
                <tr className="bg-[#2D2A4A] text-white">
                  <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider border-r border-[#3D3A5A]" style={{ width: '70px' }}>Orario</th>
                  {teachers.map(t => (
                    <th key={t.id} className="px-2 py-2 text-left font-semibold uppercase tracking-wider border-r border-[#3D3A5A]" title={`${t.name} · ${t.instrument || '—'}`}>
                      <div className="font-semibold truncate">{t.name}</div>
                      {t.instrument && <div className="text-[10px] font-normal opacity-80 truncate">{t.instrument}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.map((s, sIdx) => {
                  const isHourStart = s.endsWith(':00');
                  const isNowSlot = sIdx === currentSlotIdx;
                  return (
                    <tr
                      key={s}
                      className={`${isHourStart ? 'border-t-2 border-[#2D2A4A]/30' : 'border-t border-[#F4F4F5]'} ${isNowSlot ? 'ring-2 ring-inset ring-red-500' : ''}`}
                      data-testid={isNowSlot ? 'portineria-now-row' : undefined}
                    >
                      <td className={`px-2 py-1 border-r border-[#E4E4E7] font-mono ${isNowSlot ? 'bg-red-500 text-white font-bold' : isHourStart ? 'font-bold text-[#2D2A4A]' : 'text-[#A1A1AA]'}`}>
                        {isNowSlot ? <span className="inline-flex items-center gap-1">● {s}</span> : s}
                      </td>
                      {teachers.map(t => {
                        const cell = cellMap[`${t.id}|${sIdx}`];
                        if (!cell) {
                          return <td key={t.id} className="border-r border-[#F4F4F5] px-1 py-1 h-7"></td>;
                        }
                        if (!cell.is_start) return null;
                        const bg = STUDENT_COLORS[cell.studentIdx % STUDENT_COLORS.length];
                        return (
                          <td
                            key={t.id}
                            rowSpan={cell.rowSpan}
                            className="border-r border-[#E4E4E7] px-2 py-1 align-middle"
                            style={{ backgroundColor: bg }}
                            title={`${cell.student_name} · ${cell.start_time}–${cell.end_time}${cell.is_prova ? ' (PROVA)' : ''}`}
                          >
                            <div className="font-mono text-[10px] font-bold text-[#2D2A4A] tracking-tight leading-tight">
                              {cell.start_time}<span className="opacity-60">–</span>{cell.end_time}
                            </div>
                            <div className="font-semibold text-[#0A0A0A] uppercase truncate text-[11px] leading-tight">
                              {cell.student_name}
                            </div>
                            {cell.is_prova && (
                              <div className="inline-block mt-0.5 px-1 py-0.5 bg-amber-500 text-white text-[8px] font-extrabold uppercase tracking-wider rounded">PROVA</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
